import type {CaseReducer, PayloadAction} from '@reduxjs/toolkit'

import type {PipelineId} from '../../../../../types'
import type {NewIntegrationDto} from '../../services/pipelinesApi.types'
import {integrations} from '../SettingsSidebarContent/Integrations/Integrations.consts'

import type {PipelineDraftState} from './EditPipelinePage.slices.types'
import {
  getJob,
  deleteIfEmpty,
  getNextIntegrationId,
  filterIntegrations,
} from './EditPipelinePage.slices.utils'

export const deleteIntegration: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    id: string
    name: string
  }>
> = (state, action) => {
  const {pipelineId, id, name} = action.payload
  state[pipelineId] ??= {}
  const jobs = state[pipelineId]!.draft?.settings.jobs

  Object.keys(jobs ?? []).forEach(key => {
    const allIntegrations = jobs![key].integrations
    const hasIntegration = filterIntegrations(allIntegrations, id, name)

    if (hasIntegration) {
      jobs![key].integrations = allIntegrations?.filter(integration => {
        if (typeof integration === 'object') {
          return integration.id !== id
        } else {
          return integration !== name
        }
      })

      if (!jobs![key].integrations?.length) {
        deleteIfEmpty(jobs![key], 'integrations')
      }
    }
  })

  state[pipelineId]!.deleted ??= {}
  state[pipelineId]!.deleted!.integrations ??= []
  state[pipelineId]!.deleted!.integrations!.push(id)
}

export const restoreIntegration: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    id: string
  }>
> = (state, action) => {
  const {pipelineId, id} = action.payload
  const {deleted} = state[pipelineId] ?? {}
  if (deleted?.integrations?.includes(id)) {
    deleted.integrations = deleted.integrations.filter(featureId => id !== featureId)
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    deleteIfEmpty(deleted, 'integrations') && deleteIfEmpty(state[pipelineId], 'deleted')
  }
}

export type AddIntegrationPayload = NewIntegrationDto & {
  pipelineId: PipelineId
  jobId?: string | null
}
export const addIntegration: CaseReducer<
  PipelineDraftState,
  PayloadAction<AddIntegrationPayload>
> = (state, action) => {
  const {pipelineId, jobId, type, parameters, projectExternalId} = action.payload
  const {draft} = state[pipelineId] ?? {}

  if (draft != null) {
    const id = getNextIntegrationId(draft, type)
    draft.integrations ??= []
    draft.integrations!.push({
      id,
      type,
      parameters: {
        ...integrations[type].defaultParameters,
        ...parameters,
      },
      projectExternalId,
    })
    if (jobId != null) {
      const job = getJob(state, pipelineId, jobId)
      if (job != null && id != null) {
        job.integrations ??= []
        job.integrations!.push({id, type})
      }
    }
  }
}

export const addIntegrationWithId: CaseReducer<
  PipelineDraftState,
  PayloadAction<NewIntegrationDto & {pipelineId: PipelineId}>
> = (state, action) => {
  const {pipelineId, id, type, parameters, projectExternalId} = action.payload
  const {draft} = state[pipelineId] ?? {}

  if (draft != null && id != null) {
    draft.integrations ??= []
    // Check if integration with this ID already exists
    const existingIndex = draft.integrations.findIndex(integration => integration.id === id)
    const newIntegration = {
      id,
      type,
      parameters,
      projectExternalId,
    }

    if (existingIndex >= 0) {
      // Update existing integration
      draft.integrations[existingIndex] = newIntegration
    } else {
      // Add new integration
      draft.integrations.push(newIntegration)
    }
  }
}

export const editIntegration: CaseReducer<
  PipelineDraftState,
  PayloadAction<
    Partial<NewIntegrationDto> & {
      pipelineId: PipelineId
    }
  >
> = (state, action) => {
  const {pipelineId, id, parameters} = action.payload
  const integration = state[pipelineId]?.draft?.integrations?.find(item => item.id === id)
  if (integration != null) {
    Object.assign(integration.parameters, parameters)
  }
}

export const removeIntegration: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    id: string
  }>
> = (state, action) => {
  const {pipelineId, id} = action.payload
  const {draft} = state[pipelineId] ?? {}

  if (draft?.integrations) {
    draft.integrations = draft.integrations.filter(integration => integration.id !== id)
    if (!draft.integrations.length) {
      deleteIfEmpty(draft, 'integrations')
    }
  }
}

export const toggleIntegration: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    jobId: string
    id: string
    displayName: string | null
    disabled: boolean
    type: string
  }>
> = (state, action) => {
  const {pipelineId, jobId, id, disabled, type, displayName} = action.payload
  const job = getJob(state, pipelineId, jobId)
  if (job != null) {
    if (disabled) {
      if (filterIntegrations(job.integrations, id, displayName)) {
        job.integrations = job.integrations?.filter(integration => {
          if (typeof integration === 'object') {
            return integration.id !== id
          } else {
            return integration !== displayName
          }
        })

        if (!job.integrations?.length) {
          deleteIfEmpty(job, 'integrations')
        }
      }
    } else {
      job.integrations ??= []
      if (filterIntegrations(job.integrations, id, displayName)?.length === 0) {
        job.integrations.push({id, type})
      }
    }
  }
}
